import { VALIDATORS } from "./constants";
import { TODAY_BOUND, resolveDateBound } from "./validators";

const WHITELISTED_PROPS = [
  "picker",
  "format",
  "rows",
  "maxLength",
  "minLength",
  "step",
  "min",
  "max",
  "placeholder",
  "disabled",
  "showTime",
  "allowClear",
];

const isNil = (value) => value === null || value === undefined;

const laterDate = (left, right) => {
  if (!left) return right;
  if (!right) return left;
  return left.getTime() >= right.getTime() ? left : right;
};

const isInclusiveTodayMin = (bound) => {
  if (!bound || typeof bound !== "object") return false;
  if (bound.mode && bound.mode !== "duration") return false;
  if (Number(bound.amount) !== 0) return false;
  const unit = String(bound.unit || "day").toLowerCase();
  return unit === "d" || unit === "day" || unit === "days";
};

const shouldRaiseMinWithPlanDelta = (config) =>
  Boolean(config && config.usePlanDeltas) &&
  !isInclusiveTodayMin(config.minDate);

const earlierDate = (left, right) => {
  if (!left) return right;
  if (!right) return left;
  return left.getTime() <= right.getTime() ? left : right;
};

export const compileDisabledDate = (
  inputConfig = {},
  now = new Date(),
  options = {},
) => {
  if (!inputConfig || typeof inputConfig !== "object") return undefined;

  let minDate = !isNil(inputConfig.minDate)
    ? resolveDateBound(inputConfig.minDate, now)
    : inputConfig.disablePast
      ? resolveDateBound(TODAY_BOUND, now)
      : null;
  let maxDate = !isNil(inputConfig.maxDate)
    ? resolveDateBound(inputConfig.maxDate, now)
    : inputConfig.disableFuture
      ? resolveDateBound(TODAY_BOUND, now)
      : null;
  if (shouldRaiseMinWithPlanDelta(inputConfig) && !isNil(options.deltaStart)) {
    minDate = laterDate(
      minDate,
      resolveDateBound(
        {
          mode: "duration",
          unit: "day",
          amount: Number(options.deltaStart) || 0,
        },
        now,
      ),
    );
  }
  if (
    inputConfig.usePlanDeltas &&
    !isNil(options.deltaEnd) &&
    Number(options.deltaEnd) > 0
  ) {
    maxDate = earlierDate(
      maxDate,
      resolveDateBound(
        {
          mode: "duration",
          unit: "day",
          amount: Number(options.deltaEnd),
        },
        now,
      ),
    );
  }
  const weekdays = Array.isArray(inputConfig.disabledWeekdays)
    ? inputConfig.disabledWeekdays
    : [];

  if (!minDate && !maxDate && !weekdays.length) return undefined;

  return (current) => {
    if (!current) return false;
    const date =
      typeof current.toDate === "function" ? current.toDate() : current;
    const year = date.getFullYear();
    const month = date.getMonth();
    const dayOfMonth = date.getDate();
    const time = Date.UTC(year, month, dayOfMonth);
    if (minDate && time < minDate.getTime()) return true;
    if (maxDate && time > maxDate.getTime()) return true;
    if (weekdays.length) {
      const day =
        typeof current.day === "function" ? current.day() : date.getDay();
      if (weekdays.indexOf(day) >= 0) return true;
    }
    return false;
  };
};

export const resolveInputProps = (inputConfig = {}, nowOrOptions) => {
  if (!inputConfig || typeof inputConfig !== "object") return {};
  const options =
    nowOrOptions instanceof Date || nowOrOptions === undefined
      ? { now: nowOrOptions || new Date() }
      : nowOrOptions;
  const now = options.now || new Date();
  const props = {};
  const source = inputConfig.props || {};
  for (const key of WHITELISTED_PROPS) {
    if (!isNil(source[key])) props[key] = source[key];
  }
  const disabledDate = compileDisabledDate(inputConfig, now, options);
  if (disabledDate) props.disabledDate = disabledDate;
  return props;
};

const ruleDateBound = (rules, validator, now) => {
  const rule = (rules || []).find(
    (item) => item && item.validator === validator,
  );
  return rule ? resolveDateBound(rule.value, now) : null;
};

const toHtmlDate = (date) => (date ? date.toISOString().slice(0, 10) : "");

export const resolveDateInputBounds = (inputConfig = {}, options = {}) => {
  const config =
    inputConfig && typeof inputConfig === "object" ? inputConfig : {};
  const now = options.now || new Date();
  const rules = options.rules || [];

  let minDate = !isNil(config.minDate)
    ? resolveDateBound(config.minDate, now)
    : config.disablePast
      ? resolveDateBound(TODAY_BOUND, now)
      : ruleDateBound(rules, VALIDATORS.DATE_MIN, now);

  let maxDate = !isNil(config.maxDate)
    ? resolveDateBound(config.maxDate, now)
    : config.disableFuture
      ? resolveDateBound(TODAY_BOUND, now)
      : ruleDateBound(rules, VALIDATORS.DATE_MAX, now);

  if (shouldRaiseMinWithPlanDelta(config) && !isNil(options.deltaStart)) {
    minDate = laterDate(
      minDate,
      resolveDateBound(
        {
          mode: "duration",
          unit: "day",
          amount: Number(options.deltaStart) || 0,
        },
        now,
      ),
    );
  }
  if (
    config.usePlanDeltas &&
    !isNil(options.deltaEnd) &&
    Number(options.deltaEnd) > 0
  ) {
    maxDate = earlierDate(
      maxDate,
      resolveDateBound(
        {
          mode: "duration",
          unit: "day",
          amount: Number(options.deltaEnd),
        },
        now,
      ),
    );
  }

  const bounds = {};
  if (minDate) bounds.min = toHtmlDate(minDate);
  if (maxDate) bounds.max = toHtmlDate(maxDate);
  return bounds;
};

export const usesPlanDeltas = (inputConfig) =>
  Boolean(inputConfig && inputConfig.usePlanDeltas);

export const applyDateInputBounds = (el, bounds = {}) => {
  if (!el) return;
  if (bounds.min) el.setAttribute("min", bounds.min);
  else el.removeAttribute("min");
  if (bounds.max) el.setAttribute("max", bounds.max);
  else el.removeAttribute("max");
};
